/**
 * ⭐ PremiumContext - Verwaltet Premium-Status und Paywall
 * 
 * Server-Only Version: Premium-Status wird über die Server-API verwaltet.
 * 
 * Features:
 * - Premium-Status wird auf dem Server gespeichert (Account-gebunden!)
 * - 7 Tage kostenlose Testphase für neue User
 * - Prüft ob User Premium hat
 * - Backdoor für wiesn2022@gmail.com
 * - Google Play Billing via RevenueCat Integration
 */
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getPremiumStatus, startTrial as serverStartTrial, savePurchase, deleteAllUserData } from '../services/serverDataService';
import { setCustomFeatures } from '../services/customFeatures'; // 🧩 Kunden-Sonderfunktionen pro Konto
import { isSetupEnabled, handleServerProfile, resetProfileSync } from '../services/accountProfile'; // 🧭 Konto-Profil (Setup)
import PurchaseService from '../services/PurchaseService';
import { setCloudSyncStatus, clearCloudSyncStatus } from '../utils/premiumSyncGuard';

const PremiumContext = createContext();

// Backdoor E-Mails (immer Premium)
const PREMIUM_BACKDOOR_EMAILS = [
  'wiesn2022@gmail.com',
  'philipp.rank1987@googlemail.com',
];

// Trial-Dauer in Tagen
const TRIAL_DURATION_DAYS = 7;

// Premium Features
export const PREMIUM_FEATURES = {
  CLOUD_SYNC: 'cloud_sync',
  MULTI_DEVICE: 'multi_device',
  TABLET_MODE: 'tablet_mode',
  DOCUMENT_MANAGEMENT: 'document_management',
};

export function PremiumProvider({ children, userEmail }) {
  const [isPremium, setIsPremium] = useState(false);
  const [premiumUntil, setPremiumUntil] = useState(null);
  const [premiumPlan, setPremiumPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // 🎁 Trial States
  const [isTrial, setIsTrial] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(0);
  const [trialExpired, setTrialExpired] = useState(false);
  // 💳 Purchase States
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [products, setProducts] = useState(null);
  // Aktueller Premium-Stand für Listener/Timer (State wäre dort veraltet)
  const premiumRef = useRef(false);
  useEffect(() => { premiumRef.current = isPremium; }, [isPremium]);

  useEffect(() => {
    // RevenueCat initialisieren
    PurchaseService.initializePurchases(userEmail).then(() => {
      // Produkte laden
      PurchaseService.getProducts().then(setProducts);
    });
    
    checkPremiumStatus();

    // 🔄 Nach serverseitiger Kaufprüfung (verifyPurchase) Status sofort neu laden
    const onRefresh = () => { checkPremiumStatus(); };
    window.addEventListener('kiosk:premium-refresh', onRefresh);

    // 🔄 Solange KEIN Premium aktiv ist, leise nachsehen, ob das Konto inzwischen freigeschaltet
    // wurde: beim Zurückkehren in die App und alle 5 Minuten. Grund (01.10.2026): Abo wurde auf
    // einem Gerät gekauft, das zweite Gerät lief durch und blieb bis zum App-Neustart gesperrt.
    let letzterCheck = 0;
    let letzterProfilSync = 0;
    const syncProfileVomServer = () => {
      if (!userEmail || !isSetupEnabled()) return;
      if (Date.now() - letzterProfilSync < 12000) return;
      letzterProfilSync = Date.now();
      getPremiumStatus().then((status) => {
        if (!status) return;
        setCustomFeatures(status.customFeatures || {});
        handleServerProfile(status.profile);
      }).catch(() => { /* offline */ });
    };
    const leisePruefen = () => {
      if (document.visibilityState !== 'visible') return;
      // 🧭 Auch wenn bereits Premium/Trial aktiv ist: beim Aufwachen des Geräts (z. B. Handy aus Standby)
      // das Konto-Profil vom Server abgleichen, falls während des Schlafens am PC Einstellungen (z. B. Unterschrift) geändert wurden!
      syncProfileVomServer();
      if (premiumRef.current) return;
      if (Date.now() - letzterCheck < 30000) return;
      letzterCheck = Date.now();
      PurchaseService.syncOwnedToServer(); // eigener Kauf auf DIESEM Gerät noch nicht gemeldet?
      recheckPremium();
    };
    const onSseConnected = () => { syncProfileVomServer(); };
    document.addEventListener('visibilitychange', leisePruefen);
    window.addEventListener('kiosk:sse-connected', onSseConnected);
    const timer = setInterval(leisePruefen, 5 * 60 * 1000);
    // 🧭 Profil auf einem anderen Geraet geaendert (Server-Ereignis): sofort uebernehmen — vorher kam eine Aenderung
    // (z. B. neue Unterfaecher) erst beim naechsten App-Start an (Folkert, 10.10.2026: Etikett "Vak S" statt "S1")
    let sseClose = null;
    (async () => {
      try {
        const { connectSSE } = await import('../services/apiService');
        sseClose = connectSSE({
          'profile:updated': () => {
            if (!isSetupEnabled()) return;
            letzterProfilSync = Date.now();
            getPremiumStatus().then((status) => { setCustomFeatures(status?.customFeatures || {}); handleServerProfile(status?.profile); }).catch(() => { /* offline */ });
          },
        });
      } catch { /* kein SSE → wie bisher */ }
    })();

    return () => {
      window.removeEventListener('kiosk:premium-refresh', onRefresh);
      document.removeEventListener('visibilitychange', leisePruefen);
      window.removeEventListener('kiosk:sse-connected', onSseConnected);
      clearInterval(timer);
      try { if (sseClose) sseClose(); } catch { /* */ }
    };
  }, [userEmail]);

  /**
   * 🔄 Leise prüfen, ob das Konto inzwischen ein bezahltes Abo hat (ohne Lade-Flackern).
   * Nur wenn ja, wird der volle Status neu geladen. Gibt den aktuellen Premium-Stand zurück.
   */
  const recheckPremium = async () => {
    if (premiumRef.current) return true;
    if (!userEmail) return false;
    try {
      const status = await getPremiumStatus();
      const until = status?.until ? new Date(status.until) : null;
      const bezahlt = !!status?.isPremium && status.plan !== 'trial' && (!until || until > new Date());
      if (!bezahlt) return false;
      await checkPremiumStatus(); // setzt isPremium (und damit premiumRef) aus dem Serverstand
      return true;
    } catch {
      return false; // offline o. ä. → Stand bleibt
    }
  };

  const checkPremiumStatus = async () => {
    setIsLoading(true);
    
    // Reset trial states
    setIsTrial(false);
    setTrialDaysLeft(0);
    setTrialExpired(false);
    
    // 1. Backdoor Check (immer zuerst)
    if (userEmail && PREMIUM_BACKDOOR_EMAILS.includes(userEmail.toLowerCase())) {
      console.log('⭐ Premium via Backdoor:', userEmail);
      setIsPremium(true);
      setPremiumUntil(null); // Unbegrenzt
      setPremiumPlan('backdoor');
      setCloudSyncStatus(true, 'backdoor', null);
      setIsLoading(false);
      // 🧭 Setup-Vorschau: auch diese Konten brauchen Profil + Funktionen vom Server
      // (ohne Vorschau-Schalter wie bisher: kein Abruf).
      if (isSetupEnabled()) {
        getPremiumStatus().then((status) => {
          setCustomFeatures(status?.customFeatures || {});
          handleServerProfile(status?.profile);
        }).catch(() => { /* offline → Stand bleibt */ });
      }
      return;
    }

    // 2. Kein User eingeloggt -> kein Premium
    if (!userEmail) {
      setIsPremium(false);
      setPremiumUntil(null);
      setPremiumPlan(null);
      clearCloudSyncStatus();
      resetProfileSync(); // 🧭 kein Abgleich ohne Konto
      setIsLoading(false);
      return;
    }

    // 3. Server Premium Check (Account-gebunden!)
    try {
      const status = await getPremiumStatus();
      setCustomFeatures(status?.customFeatures || {}); // 🧩 pro Konto (z. B. Green Goat)
      handleServerProfile(status?.profile); // 🧭 Konto-Profil aufs Gerät übernehmen (nur mit Setup)
      
      // 3a. Check Paid Premium
      if (status.isPremium && status.plan !== 'trial') {
        const untilDate = status.until ? new Date(status.until) : null;
        
        if (!untilDate || untilDate > new Date()) {
          console.log('⭐ Premium vom Server geladen:', userEmail, 'bis', untilDate);
          setIsPremium(true);
          setPremiumUntil(untilDate);
          setPremiumPlan(status.plan || 'unknown');
          setCloudSyncStatus(true, 'premium', untilDate ? untilDate.toISOString() : null);
          setIsLoading(false);
          return;
        } else {
          // Premium abgelaufen
          console.log('⚠️ Premium abgelaufen für:', userEmail);
        }
      }
      
      // 3b. Check Trial
      if (status.trialEndsAt) {
        const trialUntil = new Date(status.trialEndsAt);
        
        if (trialUntil > new Date()) {
          // Trial noch aktiv!
          const daysLeft = Math.ceil((trialUntil - new Date()) / (1000 * 60 * 60 * 24));
          console.log('🎁 Trial aktiv für:', userEmail, 'noch', daysLeft, 'Tage');
          setIsPremium(true);
          setIsTrial(true);
          setTrialDaysLeft(daysLeft);
          setPremiumUntil(trialUntil);
          setPremiumPlan('trial');
          setCloudSyncStatus(true, 'trial', trialUntil.toISOString());
          setIsLoading(false);
          return;
        } else {
          // Trial abgelaufen
          console.log('⚠️ Trial abgelaufen für:', userEmail);
          setTrialExpired(true);
          setCloudSyncStatus(false, 'expired', trialUntil.toISOString());
        }
      }
        
      // 3c. Kein Premium und kein Trial → Trial starten
      if (!status.isPremium && !status.trialEndsAt) {
        console.log('🎁 Starte Trial für neuen User:', userEmail);
        await startTrial();
        return;
      }
    } catch (error) {
      console.error('Fehler beim Laden des Premium-Status aus Firebase:', error);
      // Bei Fehler: LocalStorage als Fallback (für Offline-Nutzung)
      const storedPremium = localStorage.getItem('premiumStatus');
      if (storedPremium) {
        try {
          const { active, until } = JSON.parse(storedPremium);
          const untilDate = until ? new Date(until) : null;
          
          if (active && (!untilDate || untilDate > new Date())) {
            setIsPremium(true);
            setPremiumUntil(untilDate);
            setIsLoading(false);
            return;
          }
        } catch (e) {
          console.error('LocalStorage Premium parse error:', e);
        }
      }
    }

    // 4. Kein Premium
    setIsPremium(false);
    setPremiumUntil(null);
    setPremiumPlan(null);
    setCloudSyncStatus(false, 'none', null);
    setIsLoading(false);
  };

  // 🎁 Trial starten für neue User
  const startTrial = async () => {
    const trialUntil = new Date();
    trialUntil.setDate(trialUntil.getDate() + TRIAL_DURATION_DAYS);

    try {
      await serverStartTrial(trialUntil.toISOString());
      console.log('🎁 Trial gestartet für:', userEmail, 'bis', trialUntil);
    } catch (error) {
      console.error('Fehler beim Starten des Trials:', error);
    }

    setIsPremium(true);
    setIsTrial(true);
    setTrialDaysLeft(TRIAL_DURATION_DAYS);
    setPremiumUntil(trialUntil);
    setPremiumPlan('trial');
    setCloudSyncStatus(true, 'trial', trialUntil.toISOString());
    setIsLoading(false);
  };

  // Prüft ob ein bestimmtes Feature verfügbar ist
  const hasFeature = (feature) => {
    if (isPremium) return true;
    
    // Diese Features sind immer kostenlos
    const freeFeatures = ['ocr', 'packages', 'shelves', 'search', 'dark_mode', 'languages'];
    if (freeFeatures.includes(feature)) return true;
    
    return false;
  };

  // Premium aktivieren (nach Kauf) - speichert auf Server!
  const activatePremium = async (durationDays, plan) => {
    const until = new Date();
    until.setDate(until.getDate() + durationDays);
    
    const premiumData = {
      active: true,
      until: until.toISOString(),
      activatedAt: new Date().toISOString(),
      plan: plan,
    };

    // Auf Server speichern (Account-gebunden!)
    try {
      await savePurchase(premiumData);
      console.log('⭐ Premium auf Server gespeichert für:', userEmail);
    } catch (error) {
      console.error('Fehler beim Speichern des Premium-Status auf Server:', error);
    }
    
    // Auch lokal speichern (für Offline-Nutzung)
    localStorage.setItem('premiumStatus', JSON.stringify(premiumData));
    
    setIsPremium(true);
    setPremiumUntil(until);
    setPremiumPlan(plan);
    setCloudSyncStatus(true, 'premium', until.toISOString());
  };

  // Premium deaktivieren (nach Ablauf oder Kündigung)
  const deactivatePremium = async () => {
    // Auf Server deaktivieren
    try {
      await savePurchase({
        active: false,
        cancelledAt: new Date().toISOString(),
      });
      console.log('⭐ Premium auf Server deaktiviert für:', userEmail);
    } catch (error) {
      console.error('Fehler beim Deaktivieren des Premium-Status auf Server:', error);
    }
    
    localStorage.removeItem('premiumStatus');
    setIsPremium(false);
    setPremiumUntil(null);
    setPremiumPlan(null);
    setCloudSyncStatus(false, 'cancelled', null);
  };

  // Mock-Kauf für Testing (nur für Entwicklung)
  const mockPurchase = async (plan) => {
    if (plan === 'monthly') {
      await activatePremium(30, 'monthly');
    } else if (plan === 'yearly') {
      await activatePremium(365, 'yearly');
    }
    return true;
  };

  // 💳 ECHTER KAUF via Google Play / RevenueCat
  const purchase = async (plan) => {
    if (isPurchasing) return { success: false, error: 'Kauf läuft bereits' };
    
    setIsPurchasing(true);
    
    try {
      console.log('💳 Starte Kauf:', plan);
      const result = await PurchaseService.purchaseSubscription(plan);
      
      if (result.success) {
        // Kauf erfolgreich → Premium aktivieren
        const days = plan === 'monthly' ? 30 : 365;
        await activatePremium(days, plan);
        
        // Trial-Status zurücksetzen
        setIsTrial(false);
        setTrialDaysLeft(0);
        setTrialExpired(false);
        
        console.log('💳 Kauf erfolgreich! Premium aktiviert');
        return { success: true };
      } else if (result.error === 'cancelled') {
        console.log('💳 Kauf abgebrochen');
        return { success: false, cancelled: true };
      } else {
        console.error('💳 Kauf fehlgeschlagen:', result.error);
        return { success: false, error: result.error };
      }
    } catch (error) {
      console.error('💳 Kauf-Fehler:', error);
      return { success: false, error: error.message };
    } finally {
      setIsPurchasing(false);
    }
  };

  // 💳 Käufe wiederherstellen
  const restorePurchases = async () => {
    try {
      console.log('💳 Stelle Käufe wieder her...');
      const result = await PurchaseService.restorePurchases();
      
      if (result.isPremium) {
        // Premium via wiederhergestelltem Kauf
        const expirationDate = result.expirationDate;
        const daysLeft = expirationDate 
          ? Math.ceil((expirationDate - new Date()) / (1000 * 60 * 60 * 24))
          : 365;
        
        await activatePremium(daysLeft, 'restored');
        console.log('💳 Käufe wiederhergestellt! Premium bis:', expirationDate);
        return { success: true, restored: true };
      } else {
        console.log('💳 Keine Käufe zum Wiederherstellen gefunden');
        return { success: true, restored: false };
      }
    } catch (error) {
      console.error('💳 Fehler beim Wiederherstellen:', error);
      return { success: false, error: error.message };
    }
  };

  // 💳 Abo-Verwaltung öffnen (Google Play)
  const openSubscriptionManagement = () => {
    PurchaseService.openSubscriptionManagement();
  };

  const value = {
    isPremium,
    premiumUntil,
    premiumPlan,
    isLoading,
    // 🎁 Trial
    isTrial,
    trialDaysLeft,
    trialExpired,
    // 💳 Purchase
    isPurchasing,
    products, // { monthly: { price, priceString }, yearly: { price, priceString } }
    // Methods
    hasFeature,
    activatePremium,
    deactivatePremium,
    mockPurchase, // Nur für Entwicklung
    purchase, // 💳 ECHTER KAUF
    restorePurchases, // 💳 Käufe wiederherstellen
    openSubscriptionManagement, // 💳 Abo-Verwaltung
    checkPremiumStatus,
    recheckPremium, // 🔄 leise: inzwischen freigeschaltet?
  };

  return (
    <PremiumContext.Provider value={value}>
      {children}
    </PremiumContext.Provider>
  );
}

export function usePremium() {
  const context = useContext(PremiumContext);
  if (!context) {
    throw new Error('usePremium must be used within a PremiumProvider');
  }
  return context;
}

export default PremiumContext;
